/**
 * Theme tokens ported 1:1 from the approved design's canon —
 * ../../../../swaps-design-archive/dashboard-redesign/work/docs-design/src/canon.css
 * (read-only source; copied by value, never imported at runtime).
 *
 * Zudoku's theme contract is a shadcn-shaped token set (background/foreground/card/primary/…)
 * rather than our own token names (--ink/--surface/--page/--line/…), so this file is the
 * mapping table, kept in one place instead of scattered through zudoku.config.tsx. See the
 * "token table" in the PR description for the full left→right mapping.
 */
import type { ZudokuConfig } from 'zudoku';

type ThemeTokens = NonNullable<ZudokuConfig['theme']>['light'];

export const lightTheme: ThemeTokens = {
  background: '#f1f5f9', // canon --page
  foreground: '#1d1d1f', // canon --ink
  card: '#ffffff', // canon --surface
  cardForeground: '#1d1d1f',
  popover: '#ffffff',
  popoverForeground: '#1d1d1f',
  primary: '#1d1d1f', // canon buttons are ink-filled pills
  primaryForeground: '#ffffff',
  secondary: '#f1f5f9',
  secondaryForeground: '#1d1d1f',
  muted: '#f1f5f9',
  mutedForeground: '#6e6e73', // canon --muted
  accent: '#eceef1', // canon --ink-a06 over white
  accentForeground: '#1d1d1f',
  destructive: '#b42318', // canon --crit
  destructiveForeground: '#ffffff',
  border: '#e2e8f0', // canon --line
  input: '#d2d2d7', // canon --line-strong
  ring: '#0b6fa8', // canon --accent-ink (focus ring)
  radius: '0.875rem',
};

export const darkTheme: ThemeTokens = {
  background: '#101318', // canon --page (dark)
  foreground: '#f2f3f5', // canon --ink (dark)
  card: '#1b1e23', // canon --surface (dark)
  cardForeground: '#f2f3f5',
  popover: '#1b1e23',
  popoverForeground: '#f2f3f5',
  primary: '#f2f3f5',
  primaryForeground: '#101318',
  secondary: '#1b1e23',
  secondaryForeground: '#f2f3f5',
  muted: '#1b1e23',
  mutedForeground: '#a3a9b1', // canon --muted (dark)
  accent: '#262b33', // canon --ink-a06 over dark surface
  accentForeground: '#f2f3f5',
  destructive: '#f07a70', // canon --crit (dark)
  destructiveForeground: '#101318',
  border: '#2b303a', // canon --line (dark)
  input: '#3a414c', // canon --line-strong (dark)
  ring: '#6cc1ee', // canon --accent-ink (dark)
  radius: '0.875rem',
};

/**
 * Small, additive overrides only — never a blind selector into Zudoku's internal component
 * classnames (those are not a documented contract and would be brittle across versions). This
 * covers: the canon's monospace choice for anything Zudoku already marks as code, honest
 * "planned" badge styling for the description banner the zudoku.build.ts processor injects,
 * `prefers-reduced-motion`, and a visible focus ring that matches the canon's
 * `:focus-visible { outline: 2px solid var(--accent-ink) }`.
 */
export const customCss = `
:root {
  --swaps-font-sans: "Inter", -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  --swaps-font-mono: "JetBrains Mono", "SF Mono", Menlo, monospace;
  /* Zudoku's own compiled stylesheet reads \`--font-sans\` (theme.fonts.sans, zudoku.config.tsx)
     directly in its font-family declarations — overriding the variable itself, not just \`body\`'s
     font-family, is what survives Tailwind's cascade-layer ordering (its utilities sit in a layer
     whose !important declarations outrank an unlayered !important body rule — confirmed on a live
     build: the body-selector override alone left BlinkMacSystemFont missing from the applied
     stack). */
  --font-sans: "Inter", -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", Roboto, "Helvetica Neue", sans-serif !important;


  /* Status-pill colors the shadcn token set has no slot for (canon --ok / --warn / --accent-ink) —
     used only by our own components (StatusChip, product tiles), never by Zudoku's own chrome. */
  --swaps-ok: #0a7c53;
  --swaps-ok-soft: rgba(48, 209, 88, 0.10);
  --swaps-warn: #8a5b00;
  --swaps-warn-soft: rgba(255, 159, 10, 0.12);
  --swaps-accent-ink: #0b6fa8;
  --swaps-accent-soft: rgba(47, 188, 255, 0.12);
}
@media (prefers-color-scheme: dark) {
  :root:not(.light) {
    --swaps-ok: #3ecf8e;
    --swaps-ok-soft: rgba(62, 207, 142, 0.14);
    --swaps-warn: #dfa945;
    --swaps-warn-soft: rgba(255, 183, 77, 0.14);
    --swaps-accent-ink: #6cc1ee;
    --swaps-accent-soft: rgba(47, 188, 255, 0.17);
  }
}
.dark {
  --swaps-ok: #3ecf8e;
  --swaps-ok-soft: rgba(62, 207, 142, 0.14);
  --swaps-warn: #dfa945;
  --swaps-warn-soft: rgba(255, 183, 77, 0.14);
  --swaps-accent-ink: #6cc1ee;
  --swaps-accent-soft: rgba(47, 188, 255, 0.17);
}

/* !important on both: Zudoku's own compiled stylesheet sets \`body { font-family: var(--font-sans) }\`
   from \`theme.fonts.sans\` (zudoku.config.tsx) with its own fallback chain (no BlinkMacSystemFont) —
   same selector, same specificity, so without !important the winner is whichever stylesheet the
   document happens to load second, not this one (design-parity QA 04.09, P2: BlinkMacSystemFont
   missing from the applied stack). */
body, .prose { font-family: var(--swaps-font-sans) !important; }
code, pre, kbd, .font-mono { font-family: var(--swaps-font-mono) !important; }

/* P2 (design-parity QA 04.09): Tailwind's typography plugin renders \`.prose\` body copy and
   headings at ~90% alpha (\`oklch(from var(--foreground) l c h / 90%)\`), not the solid canon ink
   these token values are meant to be — confirmed on a live build via computed style
   (\`oklch(0.2316 0.0038 none / 0.9)\`). Links are deliberately left out: they carry their own
   accent treatment, not plain ink. */
.prose,
.prose :is(h1, h2, h3, h4, h5, h6, p, li, td, th, blockquote, dt, dd, figcaption) {
  color: var(--foreground) !important;
}

/* P2: inner-page h1/h2 size and weight per the approved mock — Tailwind typography ships 36px/800
   and 24px/700 by default. */
.prose h1 { font-size: 32px !important; font-weight: 700 !important; }
.prose h2 { font-size: 20px !important; font-weight: 700 !important; }

/* P1-5 (design-parity QA 04.09): the one-permanent-sidebar container renders with a transparent
   background — confirmed on a live build (\`getComputedStyle\` returned \`rgba(0,0,0,0)\`) — so it
   reads as flush with the page instead of the mock's raised \`card\` panel. \`:has()\` scopes this to
   the one Zudoku sidebar-layout wrapper (identified structurally, via the nav it contains) rather
   than every element Tailwind's \`border-r\` utility happens to touch elsewhere on the page. */
div.border-r:has(nav.scrollbar) {
  background: var(--card) !important;
  border-right: 1px solid var(--border) !important;
}

/* P1-6 (design-parity QA 04.09): the mock inverts the code-block header bar per theme (ink bar
   with light text in light mode, near-white bar with dark text in dark mode) — Zudoku's own
   header bar is \`bg-black/4\` in both themes (a faint, non-inverting wash), confirmed on a live
   build. \`--foreground\` already IS ink-in-light / light-in-dark, and \`--background\` its inverse,
   so re-pointing bar-background at \`--foreground\` and bar-text at \`--background\` reproduces the
   mock's inversion using the existing theme tokens rather than a new hardcoded color pair. */
.code-block-wrapper > div:first-child {
  background: var(--foreground) !important;
}
.code-block-wrapper > div:first-child,
.code-block-wrapper > div:first-child button {
  color: var(--background) !important;
}
.code-block-wrapper > div:first-child button::after {
  background: var(--background) !important;
}

/* P1-6 ruling (design-parity QA round 2, 04.09) — ATTEMPTED, NOT SHIPPED. Keep Shiki's real
   syntax-highlighting palette (untouched) but move the code-block BACKGROUND to the canon
   surface tokens (dark: \`#1b1e23\`; light: white) instead of Shiki's own github-light/
   github-dark defaults. Documented here as an accepted deviation, not implemented, after three
   attempts each confirmed broken or unsafe on a real build:
   1. Overriding \`--shiki-light-bg\`/\`--shiki-dark-bg\` at :root — no effect. Shiki bakes a COPY of
      all four \`--shiki-*\`/\`--shiki-*-bg\` variables into an INLINE \`style\` attribute on every
      single highlighted \`<code class="shiki">\` element; a custom property's cascade resolves
      PER ELEMENT, so that inline copy always wins over an ancestor's :root value, regardless of
      !important there.
   2. Overriding \`background-color\` directly, unlayered, with Zudoku's own selector
      (\`.shiki\`/\`.dark .shiki\`) — also no effect. Zudoku's own consuming rule,
      \`.dark .shiki{background-color:var(--shiki-dark-bg)!important}\`, sits inside
      \`@layer base\` (checked in the built CSS). Per the CSS Cascade Layers spec, !important
      declarations invert normal layer order and rank unlayered content LAST — an unlayered
      !important rule is the weakest !important in the document and loses to any layered one.
   3. Reopening \`@layer base\` (same selector) or \`@layer theme\` (an earlier, higher-priority-
      for-!important layer, with a more specific selector to dodge exact-duplicate collapsing)
      — both altered the BUILT CSS in ways that went beyond our own rule: the first attempt's
      addition vanished from the output entirely; the second one shipped, but broke Zudoku's
      OWN dark-mode Shiki TEXT color on every code block (confirmed live — dark mode showed
      light-theme ink) even though that rule was never touched. Whatever pass merges same-named
      \`@layer\` blocks at build time (Tailwind v4 / Lightning CSS) does something with same-layer
      content beyond simple concatenation, unpredictably enough that reusing an existing layer
      name from \`theme.customCss\` is not safe here.
   No CSS path found that changes this one property without either failing silently or breaking
   an unrelated, working piece of Zudoku's own theming — real syntax highlighting matters more
   than a background-color match, so this stays Shiki's own github-light/github-dark background
   rather than risk it again. */

/* P2: code-block font-size 12.5–13px (Zudoku ships 14px, \`text-sm\`). */
.code-block-wrapper .code-block,
.code-block-wrapper .code-block code {
  font-size: 12.5px !important;
}

/* P2: inline code radius 6px (Zudoku's \`rounded-sm\` resolves to 10px against this theme's
   14px --radius). \`:not(pre) > code\` is the standard inline-vs-block distinction — code inside a
   <pre> has <pre> as its parent, so it never matches this selector. */
.prose :not(pre) > code {
  border-radius: 6px !important;
}

/* P2: the "Copy page" action renders on the plain page background (\`bg-background\`, the same
   shared Button component every toolbar button on the site uses) rather than a raised \`card\`
   surface — scoped to Zudoku's own "Page actions" toolbar landmark so this never touches an
   unrelated button that happens to share the same base component. */
[aria-label="Page actions"] button {
  background: var(--card) !important;
}

:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

/* the honest "not built yet" banner the build-time processor prepends to every non-available
   operation's description — a blockquote is the only structural hook MDX/markdown rendering
   gives us here, so it is styled to read as a status card rather than a plain quote. */
.prose blockquote:first-child {
  border-left: 3px solid var(--border);
  background: var(--muted);
  border-radius: 0 0.5rem 0.5rem 0;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--foreground);
}
.prose blockquote:first-child p { margin: 0.25rem 0; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* The header logo is the v4 lockup (src/brand.ts), as tall as the swaps.app header draws it: 26px, 22px below 1024px
   (apps/site/src/content/shared/base.css, .c-logo .c-lock), and 19px below 360px, where the header row would
   otherwise be wider than the screen. The file keeps its clear space (public/brand/v4/README.md): its viewBox is
   1207 x 350, the 1001 x 144 lockup with 103 units on every side, so the box is the whole file and only the left side
   is given back, where the header padding (16px, 32px from 1024px) already keeps it clear and the lockup lines up with
   the page. The size is set here rather than in site.logo.width, which Zudoku writes inline; the two images keep
   Zudoku's own light/dark display classes. */
header a img[src$="/swaps-lockup-ink.svg"],
header a img[src$="/swaps-lockup-paper.svg"] {
  --swaps-logo-h: 26px;
  width: calc(var(--swaps-logo-h) * 1207 / 144);
  height: calc(var(--swaps-logo-h) * 350 / 144);
  max-width: none;
  margin-left: calc(var(--swaps-logo-h) * -103 / 144);
}
@media (max-width: 1023px) {
  header a img[src$="/swaps-lockup-ink.svg"],
  header a img[src$="/swaps-lockup-paper.svg"] {
    --swaps-logo-h: 22px;
  }
}
@media (max-width: 359px) {
  header a img[src$="/swaps-lockup-ink.svg"],
  header a img[src$="/swaps-lockup-paper.svg"] {
    --swaps-logo-h: 19px;
  }
}
/* Below 768px Zudoku hides the search button's keyboard hint (its <kbd>, md:flex) but keeps the 56px right padding
   (pr-14) laid out around it. With the lockup in the header that empty padding is what pushes the header row past a
   320-375px screen, so it is given back here: the same 12px as the left. pr-14 is Zudoku's own class on that button
   (Search.tsx, the only one in its header): after a Zudoku upgrade, check the header at 320px again. */
@media (max-width: 767px) {
  header button.pr-14 {
    padding-right: 0.75rem;
  }
}

/* Architect ruling (04.09, PR #2861): the approved design is one permanent left-sidebar tree,
   never Zudoku's default top-tab-per-root-navigation-item switcher. zudoku.config.tsx already
   nests the entire nav one level inside a single wrapping root category so there is exactly one
   (lone, useless) tab instead of several — this rule hides that lone tab's row. Scoped to the
   tab list itself (a <nav> that is a direct child of the row carrying Zudoku's internal
   "--top-nav-height" custom property) so the row's other child — a compact "jump to a heading"
   control Zudoku shows at some intermediate desktop widths — is untouched, and the header's own
   brand/search/theme-toggle bar (a different, earlier element in the DOM) is untouched too. */
div[class*="top-nav-height"] > nav {
  display: none !important;
}

/* P1-7 (design-parity QA round 2, 04.09): the mock's mobile drawer is a LEFT slide-in, nav only,
   <=320px. Zudoku's MobileTopNavigation.tsx hardcodes
   \`direction={site?.dir === "rtl" ? "left" : "right"}\` — the only lever is \`site.dir\`, a real
   page-directionality setting (mirrors the whole layout for Arabic/Hebrew) we cannot repurpose
   just to flip one drawer, so there is no config path to a left drawer; documented here rather
   than in zudoku.config.tsx since the fix is entirely CSS. vaul (the drawer's underlying library,
   confirmed by reading node_modules/vaul/dist/index.mjs) only computes a JS-driven per-frame
   transform for drag gestures and snap points — neither applies here (no snap points configured)
   — so the open/close motion for our case is plain CSS \`@keyframes\` scoped to
   \`[data-vaul-drawer-direction="right"]\`, which IS safely overridable. Static position, width and
   the mirrored background-bleed pseudo-element are Tailwind classes on the same element, also
   overridable. The one true gap: vaul's DRAG-to-dismiss gesture math (JS, not CSS) still assumes
   a right-hand drawer, so a swipe-to-close gesture points the "wrong" way for a left-docked
   panel — accepted deviation, noted in the PR body; the drawer still opens, closes on backdrop
   click, and closes on any nav link tap. */
[data-vaul-drawer-direction="right"] {
  inset-inline-end: auto !important;
  inset-inline-start: 0 !important;
  width: 320px !important;
  max-width: 320px !important;
}
[data-vaul-drawer-direction="right"]::after {
  left: initial !important;
  right: 100% !important;
}
@keyframes swaps-drawer-slide-from-left {
  from { transform: translate3d(calc(var(--initial-transform, 100%) * -1), 0, 0); }
  to { transform: translate3d(0, 0, 0); }
}
@keyframes swaps-drawer-slide-to-left {
  to { transform: translate3d(calc(var(--initial-transform, 100%) * -1), 0, 0); }
}
[data-vaul-drawer][data-vaul-snap-points="false"][data-vaul-drawer-direction="right"][data-state="open"] {
  animation-name: swaps-drawer-slide-from-left !important;
}
[data-vaul-drawer][data-vaul-snap-points="false"][data-vaul-drawer-direction="right"][data-state="closed"] {
  animation-name: swaps-drawer-slide-to-left !important;
}

/* Nav only inside the drawer (P1-7): no theme toggle, no "powered by Zudoku" footer. Both live
   in the SAME last child of the drawer's own flex-col wrapper (MobileTopNavigation.tsx) — our
   docs site never configures \`authentication\`, so that footer block renders nothing else (the
   login/logout button it can also hold only appears when auth is configured), so hiding it
   loses no functionality. Scoped to \`[data-vaul-drawer]\` so this never reaches the desktop
   sidebar's own, separate "powered by" line. */
[data-vaul-drawer] > div.flex.flex-col > div:last-child {
  display: none !important;
}
`;
